(() => {
  if (customElements.get('glowup-horizon-product')) return;
  const amount = value => {
    const match = String(value || '').trim().match(/^(\d+(?:[.,]\d+)?)\s*([km])?/i);
    if (!match) return 0;
    const unit = (match[2] || '').toLowerCase();
    const number = unit ? Number(match[1].replace(',', '.')) : Number(match[1].replace(/[.,](?=\d{3}(?:\D|$))/g, '').replace(',', '.'));
    return Math.round(number * (unit === 'k' ? 1000 : unit === 'm' ? 1000000 : 1));
  };
  class GlowupProduct extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.lifecycle = new AbortController();
      const options = {signal:this.lifecycle.signal};
      this.form = this.querySelector('form');
      this.idInput = this.form.querySelector('[name="id"]');
      this.button = this.form.querySelector('[type="submit"]');
      this.picker = this.querySelector('glowup-social-picker');
      this.link = this.querySelector('[data-delivery-link]');
      this.status = this.querySelector('[data-order-status]');
      this.copy = JSON.parse(this.querySelector('[data-checkout-copy]').textContent);
      this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
      this.mediaMap = JSON.parse(this.querySelector('[data-variant-media]').textContent);
      this.form.addEventListener('change', event => {
        if (event.target.matches('[data-option]')) this.updateVariant();
      }, options);
      this.link?.addEventListener('input', () => this.updateButton(), options);
      this.addEventListener('glowup:selection', event => {
        if(event.target !== this.picker)return;
        this.selection = event.detail?.ready ? {...event.detail,variantId:String(this.variant?.id)} : null;
        this.writeProperties();
        this.updateButton();
      }, options);
      this.form.addEventListener('submit', async event => {
        if (!this.valid() || this.submitting) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.updateButton();
          if (!this.submitting) (this.link || this.picker?.input)?.focus();
          return;
        }
        event.preventDefault();
        this.writeProperties();
        const body = new FormData(this.form);
        this.submitting = true;
        this.button.disabled = true;
        this.button.textContent = this.copy.adding;
        try {
          const response=await fetch(this.form.action.replace(/\/cart\/add(?:\?.*)?$/, '/cart/add.js'),{method:'POST',body,headers:{Accept:'application/json'},signal:AbortSignal.timeout(15000)});
          const data=await response.json();
          if(!response.ok)throw new Error(data.description||this.copy.error);
          // Keep cart convenience context in this browser, not in order properties.
          if(this.selection){try{
            const saved=JSON.parse(sessionStorage.getItem('glowup-cart-profiles')||'{}');
            for(const target of this.selection.targets)saved[target.url]={profileUrl:this.selection.profileUrl,username:this.selection.username,platform:this.selection.platform};
            sessionStorage.setItem('glowup-cart-profiles',JSON.stringify(saved));
          }catch{}}
          const drawer=document.querySelector('#GlowupCartDrawer');
          if(drawer){document.dispatchEvent(new CustomEvent('glowup:cart-added'));}
          else location.assign(this.form.action.replace(/\/cart\/add(?:\?.*)?$/, '/cart'));
        } catch(error) {
          this.status.hidden=false;this.status.setAttribute('role','alert');this.status.textContent=error.message||this.copy.error;
        } finally {
          this.submitting=false;
          this.button.disabled=!this.valid();this.button.textContent=this.valid()?this.copy.add:this.copy.required;
        }
      }, {...options,capture:true});
      window.addEventListener('pageshow', () => { this.submitting=false; this.syncSelection(); }, options);
      document.addEventListener('visibilitychange',()=>{if(!document.hidden)this.syncSelection();},options);
      this.querySelectorAll('[data-media-button]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.mediaButton), options));
      this.updateVariant();
    }
    disconnectedCallback() { this.lifecycle?.abort(); this.started=false; }
    syncSelection() {
      if(this.picker?.ready && this.picker.selection){
        const s=this.picker.selection,{base,bonus}=this.quantities();
        if(s.baseQuantity===base && s.bonus===bonus)this.selection={...s,variantId:String(this.variant?.id)};
      }
      this.writeProperties();this.updateButton();
    }
    quantities() {
      const parts = String(this.variant?.options?.[0] || this.variant?.title || '').split('+');
      return {base:amount(parts[0]),bonus:amount(parts[1])};
    }
    updateVariant() {
      const selected = [...this.querySelectorAll('[data-option]:checked')].sort((a,b)=>Number(a.dataset.option)-Number(b.dataset.option)).map(input=>input.value);
      const variant = this.variants.find(item=>item.options.every((value,index)=>value===selected[index])) || (selected.length ? null : this.variants[0]);
      const changed = String(variant?.id) !== String(this.variant?.id);
      this.variant = variant;
      this.idInput.value = variant?.id || '';
      const money = value => new Intl.NumberFormat(this.dataset.lang || 'en',{style:'currency',currency:this.dataset.currency || 'EUR'}).format(value/100);
      this.querySelectorAll('[data-current-price]').forEach(node=>node.textContent=variant?money(variant.price):this.copy.unavailable);
      const compare=this.querySelector('[data-compare-price]');
      compare.hidden=!(variant?.compare_at_price>variant?.price);
      compare.textContent=compare.hidden?'':money(variant.compare_at_price);
      this.querySelectorAll('[data-option="0"]').forEach(input=>{
        const optionVariant=this.variants.find(item=>item.options[0]===input.value&&item.options.slice(1).every((v,index)=>v===selected[index+1]));
        const price=input.nextElementSibling.querySelector('[data-package-price]');
        if(price)price.textContent=optionVariant?money(optionVariant.price):this.copy.unavailable;
        input.disabled=!optionVariant?.available;
      });
      if(changed){
        this.selection=null;
        if(this.picker){
          this.picker.cancelWork?.();
          this.picker.invalidate?.();
          const {base,bonus}=this.quantities();
          this.picker.dataset.bonus=String(bonus);
          this.picker.dataset.quantity=String(base||500);
          if(this.picker.data&&(this.picker.profileSelected||this.picker.chosen?.length))this.picker.scheduleVerify?.();
        }
        this.writeProperties();
        if(this.mediaMap?.[variant?.id])this.showMedia(String(this.mediaMap[variant.id]));
        if(variant&&!this.closest('dialog')){const url=new URL(location.href);url.searchParams.set('variant',variant.id);history.replaceState({},'',url);}
      }
      this.updateButton();
    }
    valid() {
      if(!this.variant?.available)return false;
      if(this.picker){
        const s=this.selection,{base,bonus}=this.quantities();
        const age=Date.now()-Date.parse(s?.verifiedAt);
        const limit=this.picker.dataset.service==='likes'?(base>=5000?5:base>=1000?2:1):1;
        return Boolean(s?.ready && s.variantId===String(this.variant.id) && age>=0 && age<180000 && s.baseQuantity===base && s.bonus===bonus && s.total===base+bonus && s.targets?.length>=1 && s.targets.length<=limit && s.targets.every(target=>target.url&&Number.isInteger(target.quantity)&&target.quantity>0) && s.targets.reduce((total,target)=>total+target.quantity,0)===base+bonus);
      }
      if(this.link){
        const value=this.link.value.trim();
        if(!value)return false;
        return Boolean(this.contentUrl(value));
      }
      return true;
    }
    updateButton() {
      if(this.submitting)return;
      const valid=this.valid();
      this.button.disabled=!valid;
      this.button.textContent=!this.variant?.available?this.copy.unavailable:valid?this.copy.add:this.copy.required;
      this.status.textContent=valid?'':this.picker?this.copy.select_account:this.link?(this.link.nextElementSibling?.textContent||this.copy.valid_link):'';
      this.status.hidden=valid||Boolean(this.link&&!this.link.value.trim());
      if(this.link&&!valid){
        const Picker=customElements.get('glowup-social-picker');
        if(Picker){const context={inputUrl:Picker.prototype.inputUrl,platform:this.dataset.platform,platformLabel:{facebook:'Facebook',youtube:'YouTube',tiktok:'TikTok'}[this.dataset.platform],lang:(this.dataset.lang||'en').split('-')[0],message:this.status,notice:(text)=>{this.status.textContent=text;this.status.hidden=false;}};if(Picker.prototype.wrongPlatform.call(context,this.link.value))return;}
        if(this.link.value.trim())this.explainContentError();
      }
    }
    explainContentError() {
      if(this.dataset.platform==='tiktok'){
        this.status.textContent=this.copy.tiktok_invalid;this.status.hidden=false;return;
      }
      const text={
        es:['Este servicio necesita el enlace directo de la publicación o del vídeo. Un perfil o canal no identifica la publicación.','Seguidores para perfiles','Seguidores para páginas'],
        it:['Questo servizio richiede il link diretto del post o del video. Un profilo o un canale non identifica il post.','Follower per profili','Follower per pagine'],
        de:['Dieser Dienst benötigt den direkten Beitrags- oder Videolink. Ein Profil oder Kanal bezeichnet keinen einzelnen Beitrag.','Follower für Profile','Follower für Seiten'],
        en:['This service needs the direct post or video link. A profile or channel does not identify an individual post.','Profile followers','Page followers']
      }[(this.dataset.lang||'en').split('-')[0]];
      const copy=text||['Enter the direct post or video link.','Profile followers','Page followers'];
      this.status.textContent=copy[0];this.status.hidden=false;
      if(this.dataset.platform==='facebook')for(const [key,label] of [['profileService',copy[1]],['pageService',copy[2]]]){
        const href=this.dataset[key];if(!href)continue;
        const link=document.createElement('a');link.href=href;link.textContent=label;link.style.display='block';this.status.append(link);
      }
    }
    contentUrl(value) {
      if(this.dataset?.platform==='tiktok')return this.tiktokUrl(value);
      if(this.dataset?.platform!=='facebook')return this.youtubeUrl(value);
      try {
        const urls=String(value).match(/https?:\/\/[^\s<>"]+/gi);
        if(urls?.length>1)return null;
        const raw=(urls?.[0]||String(value).trim()).replace(/[)\],.!،؛]+$/u,'');
        const url=new URL(/^https?:\/\//i.test(raw)?raw:'https://'+raw);
        if(!['facebook.com','www.facebook.com','m.facebook.com','mbasic.facebook.com','web.facebook.com','fb.watch'].includes(url.hostname.toLowerCase())||url.username||url.password||url.port||!['http:','https:'].includes(url.protocol))return null;
        const path=url.pathname, token='[A-Za-z0-9._-]+';
        const video=/^\/(?:reel|reels|videos)\/[^/]+\/?$/.test(path)||/^\/[^/]+\/videos\/[^/]+\/?$/.test(path)||(/^\/watch\/?$/.test(path)&&/^\d+$/.test(url.searchParams.get('v')||''));
        const share=new RegExp('^/share/(?:p/|v/|r/)'+token+'/?$').test(path);
        const post=/^\/[^/]+\/posts\/[^/]+\/?$/.test(path)||(/^\/(?:permalink|story)\.php$/.test(path)&&/^[A-Za-z0-9]+$/.test(url.searchParams.get('story_fbid')||''));
        const photo=/^\/photo(?:\.php)?\/?$/.test(path)&&/^\d+$/.test(url.searchParams.get('fbid')||'');
        const short=url.hostname==='fb.watch'&&new RegExp('^/'+token+'/?$').test(path);
        if(!(video||share||short||(this.dataset.service!=='views'&&(post||photo))))return null;
        url.protocol='https:';url.hash='';
        for(const key of [...url.searchParams.keys()])if(!['v','story_fbid','id','fbid','set'].includes(key))url.searchParams.delete(key);
        return url.href;
      }catch{return null;}
    }
    tiktokUrl(value) {
      try {
        const input=String(value).trim();
        if(!input||input.length>4000)return null;
        const urls=input.match(/https?:\/\/[^\s<>"\u200B-\u200F\u202A-\u202E\u2066-\u2069]+/gi);
        if(urls?.length>1)return null;
        const raw=(urls?.[0]||input).replace(/[)\],.!،؛]+$/u,'');
        if(/[\\\s\u0000-\u001F]/u.test(raw))return null;
        const url=new URL(/^https?:\/\//i.test(raw)?raw:'https://'+raw);
        const host=url.hostname.toLowerCase();
        if(!(host==='tiktok.com'||host.endsWith('.tiktok.com'))||url.username||url.password||url.port||!['http:','https:'].includes(url.protocol))return null;
        if(url.pathname==='/'&&!url.search)return null;
        url.protocol='https:';
        return url.href;
      }catch{return null;}
    }
    youtubeUrl(value) {
      try {
        const urls=String(value).match(/https?:\/\/[^\s<>"]+/gi);
        let raw=urls?.length===1?urls[0].replace(/[)\],.!،؛]+$/u,''):String(value).trim();
        let url=new URL(/^https?:\/\//i.test(raw)?raw:'https://'+raw);
        for(let hop=0;hop<2;hop++){
          const host=url.hostname.toLowerCase().replace(/^www\./,'');
          if(!['youtube.com','m.youtube.com','music.youtube.com','youtu.be','youtube-nocookie.com'].includes(host)||url.username||url.password||url.port||!['http:','https:'].includes(url.protocol))return null;
          if(url.pathname==='/attribution_link'&&host.endsWith('youtube.com')){url=new URL(url.searchParams.get('u')||url.searchParams.get('q')||'', 'https://www.youtube.com');continue;}
          const parts=url.pathname.split('/').filter(Boolean);
          const id=host==='youtu.be'&&parts.length===1?parts[0]:url.pathname==='/watch'?url.searchParams.get('v'):parts.length===2&&['shorts','live','embed','v'].includes(parts[0])?parts[1]:null;
          return id&&/^[a-zA-Z0-9_-]{6,64}$/.test(id)?'https://www.youtube.com/watch?v='+id:null;
        }
      } catch {}
      return null;
    }
    properties() {
      if(!this.valid())return {};
      if(this.link){
        return {Link:this.contentUrl(this.link.value)};
      }
      if(!this.picker)return {};
      const s=this.selection;
      const values={};
      s.targets.forEach((target,index)=>{
        const key=s.targets.length===1?'Link':`Link ${index+1} (${target.quantity} ${this.picker.unit})`;
        values[key]=target.url;
      });
      return values;
    }
    writeProperties() {
      this.form.querySelectorAll('[data-glowup-property]').forEach(input=>input.remove());
      for(const [name,value] of Object.entries(this.properties())){
        const input=document.createElement('input');input.type='hidden';input.name='properties['+name+']';input.value=String(value);input.dataset.glowupProperty='true';this.form.append(input);
      }
    }
    showMedia(id) {
      if(!this.querySelector('[data-product-media="'+id+'"]'))return;
      this.querySelectorAll('[data-product-media]').forEach(media=>media.hidden=media.dataset.productMedia!==id);
      this.querySelectorAll('[data-media-button]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.mediaButton===id)));
    }
  }
  customElements.define('glowup-horizon-product',GlowupProduct);
})();
